import React, { useState } from 'react';
import { Form, Button, Card, Alert } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { Music, DollarSign } from 'react-feather';
import { createSongRequest } from '../services/api';
import {RequestFormProps} from "../types/RequestFormProps";

const RequestForm: React.FC<RequestFormProps> = ({
                                                   eventId,
                                                   eventName,
                                                   djName,
                                                   onRequestSubmitted,
                                                   className = '' // Default to empty string
                                                 }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    songName: '',
    artistName: '',
    requesterName: '',
    message: '',
    donationAmount: ''
  });
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [donationEnabled, setDonationEnabled] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.songName.trim()) {
      newErrors.songName = t('requestForm.errors.songRequired');
    }

    if (!formData.requesterName.trim()) {
      newErrors.requesterName = t('requestForm.errors.nameRequired');
    }

    if (donationEnabled && formData.donationAmount && isNaN(Number(formData.donationAmount))) {
      newErrors.donationAmount = t('requestForm.errors.invalidAmount');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitError('');

    try {
      const requestData = {
        song_name: formData.songName,
        artist_name: formData.artistName,
        requester_name: formData.requesterName,
        message: formData.message,
        donation_amount: donationEnabled && formData.donationAmount ? parseFloat(formData.donationAmount) : undefined
      };

      const response = await createSongRequest(eventId, requestData);

      setSubmitSuccess(true);
      setFormData({
        songName: '',
        artistName: '',
        requesterName: '',
        message: '',
        donationAmount: ''
      });

      if (onRequestSubmitted) {
        onRequestSubmitted(response.data?.request);
      }
    } catch (error: any) {
      setSubmitError(error.response?.data?.error || t('requestForm.errors.submissionFailed'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
      <Card className={`shadow-sm ${className}`}>
        <Card.Header className="custon-home-cta text-white">
          <div className="d-flex align-items-center">
            <Music size={20} className="me-2" />
            <h5 className="mb-0">{t('requestForm.title')}</h5>
          </div>
        </Card.Header>

        <Card.Body>
          {submitSuccess ? (
              <Alert variant="success">
                <Alert.Heading>{t('requestForm.success.title')}</Alert.Heading>
                <p>{t('requestForm.success.message', { djName })}</p>
                <Button
                    variant="outline-success"
                    onClick={() => setSubmitSuccess(false)}
                    className="mt-2 custom-purple-btn"
                >
                  {t('requestForm.makeAnother')}
                </Button>
              </Alert>
          ) : (
              <>
                <p className="text-muted mb-4">
                  {t('requestForm.eventInfo', { eventName, djName })}
                </p>

                {submitError && <Alert variant="danger">{submitError}</Alert>}

                <Form onSubmit={handleSubmit}>
                  <Form.Group className="mb-3">
                    <Form.Label>{t('requestForm.songName')}</Form.Label>
                    <Form.Control
                        type="text"
                        name="songName"
                        value={formData.songName}
                        onChange={handleChange}
                        isInvalid={!!errors.songName}
                        placeholder={t('requestForm.songNamePlaceholder') as string}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.songName}
                    </Form.Control.Feedback>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>{t('requestForm.yourName')}</Form.Label>
                    <Form.Control
                        type="text"
                        name="requesterName"
                        value={formData.requesterName}
                        onChange={handleChange}
                        isInvalid={!!errors.requesterName}
                        placeholder={t('requestForm.yourNamePlaceholder') as string}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.requesterName}
                    </Form.Control.Feedback>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>{t('requestForm.message')}</Form.Label>
                    <Form.Control
                        as="textarea"
                        rows={3}
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        placeholder={t('requestForm.messagePlaceholder') as string}
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Check
                        type="switch"
                        id="donation-toggle"
                        label={t('requestForm.enableDonation')}
                        checked={donationEnabled}
                        onChange={(e) => {
                          setDonationEnabled(e.target.checked);
                          if (!e.target.checked) {
                            setFormData(prev => ({ ...prev, donationAmount: '' }));
                          }
                        }}
                    />
                  </Form.Group>

                  {donationEnabled && (
                      <Form.Group className="mb-4">
                        <Form.Label className="d-flex align-items-center">
                          <DollarSign size={16} className="me-1" />
                          {t('requestForm.donation')}
                        </Form.Label>
                        <Form.Control
                            type="text"
                            name="donationAmount"
                            value={formData.donationAmount}
                            onChange={handleChange}
                            isInvalid={!!errors.donationAmount}
                            placeholder={t('requestForm.donationPlaceholder') as string}
                        />
                        <Form.Text className="text-muted">
                          {t('requestForm.donationInfo')}
                        </Form.Text>
                        <Form.Control.Feedback type="invalid">
                          {errors.donationAmount}
                        </Form.Control.Feedback>
                      </Form.Group>
                  )}

                  <div className="d-grid">
                    <Button
                        type="submit"
                        disabled={isSubmitting}
                        size="lg"
                        className="custom-purple-btn"// Made button larger for better mobile experience
                    >
                      {isSubmitting ? t('requestForm.submitting') : t('requestForm.submit')}
                    </Button>
                  </div>
                </Form>
              </>
          )}
        </Card.Body>
      </Card>
  );
};

export default RequestForm;
